import { useEffect, useId, useState, type FormEvent } from 'react';
import { StatusBadge } from '@pazportal/ui';
import { useEstimatorSettings, useSaveEstimatorSettings, type EstimatorDefaults } from '../hooks/useEstimatorSettings';
import { integrationBadgeStatus, m365StateLabel, useIntegrationStatus } from '../hooks/useIntegrationStatus';
import { formatDate } from '../lib/formatters';

interface FormState {
  hardwareMarginPct: string;
  laborRate: string;
  contingencyPct: string;
  validDays: string;
  proposalFooter: string;
}

function toForm(d: EstimatorDefaults | undefined): FormState {
  return {
    hardwareMarginPct: d?.hardwareMarginPct === null || d?.hardwareMarginPct === undefined ? '' : String(d.hardwareMarginPct),
    laborRate: d?.laborRate === null || d?.laborRate === undefined ? '' : String(d.laborRate),
    contingencyPct: d?.contingencyPct === null || d?.contingencyPct === undefined ? '' : String(d.contingencyPct),
    validDays: d?.validDays === null || d?.validDays === undefined ? '' : String(d.validDays),
    proposalFooter: d?.proposalFooter ?? '',
  };
}

function parseNumber(value: string, { min, max, integer }: { min: number; max: number; integer?: boolean }): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (integer && !Number.isInteger(n)) return null;
  if (n < min || n > max) return null;
  return n;
}

export function SettingsPage() {
  const { data: defaults, isLoading, error } = useEstimatorSettings();
  const save = useSaveEstimatorSettings();
  const { data: snapshot, isLoading: snapshotLoading, error: snapshotError } = useIntegrationStatus();

  const [form, setForm] = useState<FormState>(() => toForm(undefined));
  const [dirty, setDirty] = useState(false);
  const [validation, setValidation] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (defaults && !dirty) setForm(toForm(defaults));
  }, [defaults, dirty]);

  const update = (key: keyof FormState) => (value: string) => {
    setDirty(true);
    setSavedAt(null);
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setValidation(null);
    const margin = parseNumber(form.hardwareMarginPct, { min: 0, max: 99 });
    const rate = parseNumber(form.laborRate, { min: 0, max: 10_000 });
    const contingency = parseNumber(form.contingencyPct, { min: 0, max: 100 });
    const validDays = parseNumber(form.validDays, { min: 1, max: 365, integer: true });
    if (margin === null || rate === null || contingency === null || validDays === null) {
      setValidation('Enter a hardware margin (0–99%), labor rate ($/hr), contingency (0–100%) and validity (1–365 whole days).');
      return;
    }
    try {
      await save.mutateAsync({
        hardwareMarginPct: margin,
        laborRate: rate,
        contingencyPct: contingency,
        validDays,
        proposalFooter: form.proposalFooter,
      });
      setDirty(false);
      setSavedAt(new Date().toISOString());
    } catch {
      // save.error carries the message; rendered below.
    }
  };

  const m365 = snapshot?.m365;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <h1 className="text-xl font-semibold text-text">Settings</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <form onSubmit={onSubmit} className="bg-surface rounded-lg border border-border p-4 sm:p-5 space-y-5" aria-labelledby="pricing-heading" noValidate>
          <div>
            <h2 id="pricing-heading" className="text-sm font-semibold text-text-muted uppercase tracking-wider">Default pricing assumptions</h2>
            <p className="text-xs text-text-muted mt-1">
              These defaults seed every new estimate. Each estimate keeps its own values, so changing a default never rewrites an existing quote.
            </p>
          </div>

          {isLoading && <p className="text-sm text-text-muted" role="status">Loading saved defaults…</p>}
          {error && (
            <p className="text-sm text-error" role="alert">
              {error instanceof Error ? error.message : 'Could not load settings.'}
            </p>
          )}

          <NumberField
            label="Hardware margin"
            unit="%"
            value={form.hardwareMarginPct}
            onChange={update('hardwareMarginPct')}
            min={0}
            max={99}
            step={0.5}
            sliderMin={10}
            sliderMax={45}
            disabled={isLoading}
          />
          <NumberField
            label="Labor rate"
            unit="$/hr"
            value={form.laborRate}
            onChange={update('laborRate')}
            min={0}
            max={10_000}
            step={1}
            sliderMin={85}
            sliderMax={200}
            disabled={isLoading}
          />
          <NumberField
            label="Contingency"
            unit="%"
            value={form.contingencyPct}
            onChange={update('contingencyPct')}
            min={0}
            max={100}
            step={0.5}
            sliderMin={0}
            sliderMax={25}
            disabled={isLoading}
          />
          <NumberField
            label="Proposal validity"
            unit="days"
            value={form.validDays}
            onChange={update('validDays')}
            min={1}
            max={365}
            step={1}
            disabled={isLoading}
          />
          <TextAreaField
            label="Proposal footer"
            hint="Optional. Appears at the end of proposal emails (terms, payment schedule, contact line)."
            value={form.proposalFooter}
            onChange={update('proposalFooter')}
            disabled={isLoading}
          />

          {validation && (
            <p className="text-xs text-error" role="alert">{validation}</p>
          )}
          {save.error && (
            <p className="text-xs text-error" role="alert">
              Save failed: {save.error instanceof Error ? save.error.message : 'unknown error'}
            </p>
          )}
          {savedAt && !dirty && (
            <p className="text-xs text-success" role="status">Saved. New estimates now start from these values.</p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] text-text-faint">
              {defaults?.exists
                ? `Last updated ${formatDate(defaults.updatedAt)}${defaults.updatedByName ? ` by ${defaults.updatedByName}` : ''}`
                : 'No defaults saved yet.'}
            </p>
            <button
              type="submit"
              disabled={save.isPending || isLoading || !dirty}
              className="inline-flex min-h-11 items-center px-4 py-2 text-sm font-medium rounded-lg bg-primary text-text-inverse hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {save.isPending ? 'Saving…' : 'Save defaults'}
            </button>
          </div>
        </form>

        <section className="bg-surface rounded-lg border border-border p-4 sm:p-5 space-y-4" aria-labelledby="m365-heading">
          <div className="flex items-start justify-between gap-3">
            <h2 id="m365-heading" className="text-sm font-semibold text-text-muted uppercase tracking-wider">Microsoft 365</h2>
            {m365 && (
              <span className="inline-flex items-center gap-2">
                <StatusBadge status={integrationBadgeStatus(m365.state)} />
                <span className="sr-only">{m365StateLabel(m365.state)}</span>
              </span>
            )}
          </div>

          {snapshotLoading && <p className="text-sm text-text-muted" role="status">Checking integration status…</p>}
          {snapshotError && (
            <p className="text-sm text-error" role="alert">
              Integration status unavailable: {snapshotError instanceof Error ? snapshotError.message : 'request failed'}
            </p>
          )}
          {m365 && (
            <dl className="text-sm space-y-2">
              <div className="flex justify-between gap-3">
                <dt className="text-text-muted">Status</dt>
                <dd className="text-text font-medium">{m365StateLabel(m365.state)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-text-muted">Sent estimates</dt>
                <dd className="text-text">{m365.estimates_sent}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-text-muted">Tracked in mailbox</dt>
                <dd className="text-text">{m365.with_message_id}</dd>
              </div>
            </dl>
          )}
          {m365 && m365.state !== 'live' && (
            <p className="text-xs text-text-muted">
              Reported as not configured because no proposal has been delivered through Microsoft 365 from this system yet.
              Proposal sending stays disabled until the Graph connection is configured on the job runner and a send succeeds.
            </p>
          )}
          <p className="text-xs text-text-muted">
            Proposals are sent from the PazNetworks sales mailbox (sales@paznetworks.com) under an application access
            policy. Personal mailboxes cannot be connected here.
          </p>
        </section>
      </div>
    </div>
  );
}

function NumberField({
  label,
  unit,
  value,
  onChange,
  min,
  max,
  step,
  sliderMin,
  sliderMax,
  disabled,
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
  min: number;
  max: number;
  step: number;
  sliderMin?: number;
  sliderMax?: number;
  disabled?: boolean;
}) {
  const id = useId();
  const numeric = Number(value);
  const sliderValue = Number.isFinite(numeric) && value !== '' ? numeric : (sliderMin ?? min);
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={id} className="text-xs font-medium text-text-muted">{label}</label>
        <div className="flex items-center gap-1.5">
          <input
            id={id}
            type="number"
            inputMode="decimal"
            min={min}
            max={max}
            step={step}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className="w-24 min-h-11 px-2 py-1 text-sm text-right border border-border rounded-md bg-surface text-text disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
          <span className="text-xs text-text-muted w-10">{unit}</span>
        </div>
      </div>
      {sliderMin !== undefined && sliderMax !== undefined && (
        <input
          type="range"
          aria-label={`${label} slider`}
          min={sliderMin}
          max={sliderMax}
          step={step}
          value={Math.min(sliderMax, Math.max(sliderMin, sliderValue))}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="w-full mt-2 accent-[var(--primary)] disabled:opacity-50"
        />
      )}
    </div>
  );
}

function TextAreaField({
  label,
  hint,
  value,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-text-muted mb-1">{label}</label>
      <textarea
        id={id}
        rows={3}
        value={value}
        disabled={disabled}
        aria-describedby={hint ? hintId : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-border rounded-md bg-surface text-text resize-y disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      />
      {hint && <p id={hintId} className="text-[11px] text-text-faint mt-1">{hint}</p>}
    </div>
  );
}
